import { useId } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';

import { useDialogA11y } from '@/shared/hooks/useDialogA11y';

/**
 * Los dos sitios donde se pide lo mismo, que no se piden igual.
 *
 * - `signup`: en el alta, antes de que exista la cuenta. Cancelar cierra y ya.
 * - `gate`: después de entrar, a quien tiene cuenta **sin** consentimiento —hoy
 *   se puede crear una así desde `/login`, que no tiene casilla—. Ahí cancelar
 *   cierra la sesión, así que el botón lo dice, y ni Escape ni el clic fuera
 *   valen: un descuido no debe echar a nadie de la aplicación.
 *
 * Un `variant` y no tres props sueltas —título, texto del botón, si se puede
 * descartar—: son tres decisiones que siempre van juntas, y sueltas se pueden
 * combinar mal (un diálogo que echa la sesión al pulsar «Cancelar» sin avisar).
 */
type Variant = 'signup' | 'gate';

interface Props {
  onAccept: () => void;
  onCancel: () => void;
  variant?: Variant;
}

/**
 * Lo que sale al pulsar SSO sin haber marcado la casilla.
 *
 * ## Por qué existe, si la casilla ya está a la vista
 *
 * La casilla se subió encima de los botones de SSO, que era la mitad del
 * problema —antes estaba al final del formulario de correo y pulsar SSO te
 * mandaba con un salto de scroll hasta allí, al pie de unos campos que no
 * habías pedido rellenar—. Esto es la otra mitad: quien aun así no la marque no
 * se encuentra un botón que no hace nada, sino la pregunta.
 *
 * ## Aceptar es un acto, no una casilla dentro de otra
 *
 * El diálogo **no repite la casilla**: enseña lo mismo que ella y tiene dos
 * botones. Marcar una casilla dentro de una ventana que ya has abierto a
 * propósito es un gesto de más para decir lo mismo, y el «acepto» de un botón
 * es igual de afirmativo a efectos del consentimiento.
 *
 * ## Cancelar cierra, y ya
 *
 * No lleva a ninguna otra pantalla. Quien ha pulsado «Continuar con Google» en
 * el alta y se lo piensa no quiere aterrizar en el acceso: quiere seguir donde
 * estaba, con la casilla ahí para cuando decida. Mandarlo a otro sitio sería
 * más fricción, que es justo lo contrario de lo que se busca aquí.
 */
export function ConsentDialog({ onAccept, onCancel, variant = 'signup' }: Props) {
  const { t } = useTranslation();
  const titleId = useId();
  const gate = variant === 'gate';
  // En la puerta no hay salida por descuido: sin Escape y sin clic fuera.
  const panelRef = useDialogA11y(gate ? () => {} : onCancel);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={gate ? undefined : onCancel}
    >
      <div
        ref={panelRef}
        className="w-full max-w-sm rounded-lg bg-white p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={titleId} className="mb-2 flex items-center gap-2 font-serif text-base text-brand">
          <ShieldCheck size={18} />
          {t(gate ? 'auth.consent_gate_title' : 'auth.consent_dialog_title')}
        </h2>

        {/* En la puerta hace falta una línea de por qué: a quien ya tiene
            cuenta le aparece esto de la nada, y sin explicación parece un
            fallo. En el alta sobra, porque el contexto es evidente. */}
        {gate && (
          <p className="mb-3 text-sm leading-relaxed">{t('auth.consent_gate_body')}</p>
        )}

        {/* El mismo texto que la casilla, con los mismos dos enlaces: es la
            misma afirmación y no debe haber dos redacciones que mantener a la
            par a mano. Los enlaces abren en otra pestaña para que leerlos no
            cierre el diálogo ni pierda el alta a medias. */}
        <p className="text-muted mb-4 text-sm leading-relaxed">
          <Trans
            i18nKey="auth.gdpr_consent"
            components={{
              terms: (
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand hover:underline"
                />
              ),
              privacy: (
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand hover:underline"
                />
              ),
            }}
          />
        </p>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-neutral-300 px-3 py-1.5 text-sm"
          >
            {t(gate ? 'auth.sign_out' : 'common.cancel')}
          </button>
          <button
            type="button"
            onClick={onAccept}
            autoFocus
            className="btn-primary px-3 py-1.5 text-sm"
          >
            {t('auth.consent_accept')}
          </button>
        </div>
      </div>
    </div>
  );
}
